import { Fragment, useCallback, useEffect, useState } from "react";
import { useProduct } from "../product";
import { useTranslation } from "react-i18next";
import Scope from "../components/Scope";
import VerdictBar, { verdictCounts } from "../components/VerdictBar";
import Build, { refNeeded } from "../components/Build";
import RunCard from "../components/RunCard";
import { Loading, Notice, PageHead } from "../components/Look";
import { useTitle } from "../useTitle";
import History from "../components/History";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError, api, duration, on, type Bench, type Project, type Run } from "../api";
import i18n from "../i18n";

/**
 * Run history across every bench - the thing the per-bench server cannot show.
 *
 * IT SHOWED 50 OF 90 AND SAID NOTHING. `api.runs` never passed a limit, so the
 * list stopped at the server's default and the other forty were unreachable
 * from the UI; the chart above it stops at 30, for its own reasons, and said
 * nothing either. There was no filter of any kind - not by bench (that existed
 * only as a link FROM the benches page), not by outcome, not by date, no
 * search - so finding a run meant knowing roughly when it ran and counting
 * rows.
 *
 * The filters are CLIENT-SIDE, over what has been loaded, and the footer says
 * so. The endpoint takes `bench_id` and `limit` and nothing else, and a filter
 * that quietly searched the newest fifty while looking like it searched
 * everything would be worse than no filter at all.
 */

const PAGE = 50;

type Outcome = "" | "failures" | "clean";

export default function Runs() {
  const { t } = useTranslation();
  useTitle(t("runs.title"));
  const [params, setParams] = useSearchParams();
  const benchId = params.get("bench");
  // Which product (step K6). The address says so when a link does; otherwise
  // the product the reader is in - "all products" is one chip away. A bench
  // in the address wins: it already names one product's bench.
  const { id: current } = useProduct();
  const projectParam = params.get("project");
  const projectId = benchId ? null : projectParam === "all" ? null : Number(projectParam) || current || null;
  const [projects, setProjects] = useState<Project[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [runs, setRuns] = useState<Run[]>([]);
  const [benches, setBenches] = useState<Bench[]>([]);
  const [limit, setLimit] = useState(PAGE);
  const [more, setMore] = useState(false);
  const [q, setQ] = useState("");
  const [outcome, setOutcome] = useState<Outcome>("");
  const [scope, setScope] = useState<"" | "full" | "partial">("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.runs(benchId ? Number(benchId) : undefined, limit, projectId ?? undefined);
      setRuns(r.runs);
      setLoaded(true);
      // A full page back means there is probably another; a short one is the
      // end. The endpoint returns no total, and inventing one would be a
      // number that goes wrong exactly on the boundary.
      setMore(r.runs.length >= limit);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [benchId, limit, projectId]);

  useEffect(() => {
    let live = true;
    const tick = () => { if (live) void load(); };
    tick();
    const t = setInterval(tick, 10_000);
    return () => { live = false; clearInterval(t); };
  }, [load]);

  useEffect(() => {
    api.benches().then((d) => setBenches(d.benches)).catch(() => {});
    api.projects().then((d) => setProjects(d.projects.filter((p) => p.bench_count > 0 || p.run_count > 0))).catch(() => {});
  }, []);

  function pickProject(id: number | "all") {
    const p = new URLSearchParams(params);
    p.delete("bench");
    p.set("project", String(id));
    setParams(p, { replace: true });
    setLimit(PAGE);
  }
  function pickBench(id: number | null) {
    const p = new URLSearchParams(params);
    if (id) p.set("bench", String(id)); else p.delete("bench");
    setParams(p);
    setLimit(PAGE);
  }

  // The chart reads one bench's history; a product with one bench is that bench.
  const mineBenches = projectId ? benches.filter((b) => b.project_id === projectId) : [];
  const productBench = mineBenches.length === 1 ? mineBenches[0].id : undefined;
  const shown = runs.filter((r) => {
    if (q.trim() && !r.name.toLowerCase().includes(q.trim().toLowerCase())) return false;
    if (scope && r.scope !== scope) return false;
    if (outcome === "failures" && verdictCounts(r).failed === 0) return false;
    if (outcome === "clean" && verdictCounts(r).failed > 0) return false;
    return true;
  });
  const filtering = q.trim() !== "" || outcome !== "" || scope !== "";
  // The column appears when there is a build to put in it. Every run recorded
  // before harness v0.1.8 has none, so a column added unconditionally would be
  // ninety rows of "not recorded" - which is what `State` was, and the reason
  // it is gone. It turns itself on as the new runs arrive.
  const anyBuild = runs.some((r) => r.build_recorded);

  // The answer: how much of the history is on the page, and how much of it
  // went wrong - the question somebody opens a list of runs with.
  const withFailures = shown.filter((r) => verdictCounts(r).failed > 0).length;

  return (
    <section>
      <PageHead
        title={t("runs.heading")}
        answer={
          runs.length > 0
            ? `${t("runs.answerLoaded", { count: shown.length })} ${
                withFailures
                  ? t("runs.answerFailures", { count: withFailures })
                  : t("runs.answerNoFailures")
              }`
            : undefined
        }
        bad={withFailures > 0}
      />
      {!loaded && <Loading label={t("common.loading")} />}

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        <input
          className="min-w-[12rem] flex-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
          placeholder={t("runs.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label={t("runs.searchLabel")}
        />
        <Chip on={!benchId && !projectId} onClick={() => pickProject("all")}>{t("runs.allProducts")}</Chip>
        {projects.map((p) => (
          <Chip key={p.id} on={!benchId && projectId === p.id} onClick={() => pickProject(p.id)}>
            {p.name}
          </Chip>
        ))}
        <span className="mx-1 h-4 w-px bg-slate-200 dark:bg-slate-700" />
        {benches.filter((b) => !projectId || b.project_id === projectId).map((b) => (
          <Chip key={b.id} on={benchId === String(b.id)} onClick={() => pickBench(b.id)}>
            {b.name}
          </Chip>
        ))}
        <span className="mx-1 h-4 w-px bg-slate-200 dark:bg-slate-700" />
        <Chip on={outcome === "failures"}
              onClick={() => setOutcome(outcome === "failures" ? "" : "failures")}>
          {t("runs.hasFailures")}
        </Chip>
        <Chip on={outcome === "clean"}
              onClick={() => setOutcome(outcome === "clean" ? "" : "clean")}>
          {t("runs.clean")}
        </Chip>
        <Chip on={scope === "full"} onClick={() => setScope(scope === "full" ? "" : "full")}>
          {t("runs.wholeSuite")}
        </Chip>
        <Chip on={scope === "partial"} onClick={() => setScope(scope === "partial" ? "" : "partial")}>
          {t("runs.slices")}
        </Chip>
      </div>

      {error && <Notice tone="error">{error}</Notice>}

      {loaded && runs.length === 0 && !error && (
        <p className="text-base text-slate-500">{t("runs.empty")}</p>
      )}

      <History benchId={benchId ? Number(benchId) : productBench} />

      {/* A table on a desk, cards on a phone. `overflow-x-auto` meant that at
          390 px the columns on screen were Run, Bench and Started - and Failed,
          the one column somebody checks their phone FOR, sat off the right edge
          behind a scroll nothing suggested. */}
      <div className="hidden overflow-hidden rounded-lg border border-slate-200 sm:block dark:border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-left text-xs font-medium text-slate-500 dark:bg-slate-800">
            <tr>
              <th scope="col" className="px-3 py-2">{t("runs.colRun")}</th>
              <th scope="col" className="px-3 py-2">{t("runs.colBench")}</th>
              {anyBuild && (
                <th scope="col" className="px-3 py-2">{t("runs.colBuild")}</th>
              )}
              <th scope="col" className="px-3 py-2">{t("runs.colStarted")}</th>
              <th scope="col" className="px-3 py-2">{t("runs.colTook")}</th>
              <th scope="col" className="px-3 py-2 text-right">{t("runs.colSteps")}</th>
              <th scope="col" className="px-3 py-2">{t("runs.colResult")}</th>
            </tr>
          </thead>
          <tbody>
            {byDay(shown).map(([day, rows]) => (
              <Day key={day} day={day} span={anyBuild ? 7 : 6}>
                {byCampaign(rows).map((g) => (
                  <Fragment key={g.key}>
                    {/* A campaign is one decision that became four runs, and
                        the list showed them as four unrelated rows that happen
                        to sit next to each other. The stops are already
                        adjacent here, because the list is in time order, so the
                        group needs a heading and not a re-sort. */}
                    {g.campaign !== null && (
                      <tr>
                        <th
                          scope="colgroup"
                          colSpan={anyBuild ? 7 : 6}
                          className="border-t border-slate-200 bg-sky-50/70 px-3 py-1 text-left text-xs font-medium text-slate-600 dark:border-slate-800 dark:bg-slate-800/30 dark:text-slate-300"
                        >
                          <Link className="underline" to={`/campaigns/${g.campaign}`}>
                            {t("campaign.badge", { id: g.campaign })}
                          </Link>
                          <span className="ml-2 font-normal text-slate-500">
                            {t("campaign.stops", { count: g.runs.length })}
                          </span>
                        </th>
                      </tr>
                    )}
                    {g.runs.map((r) => (
                  <tr key={r.id}
                      className="border-t border-slate-200 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/50">
                    <td className="px-3 py-2 font-medium">
                      <span className="flex flex-wrap items-center gap-2">
                        <Link className="hover:underline" to={`/runs/${r.id}`}>{r.name}</Link>
                        <Scope run={r} />
                      </span>
                      {r.reason && (
                        <span className="mt-0.5 block text-xs font-normal text-slate-500">{r.reason}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-400">{r.bench_name}</td>
                    {anyBuild && (
                      <td className="px-3 py-2">
                        {r.build_recorded ? (
                          <Build build={r.build} ref_={r.build_ref} recorded
                                 showRef={refNeeded(shown).has(r.id)}
                                 variant={r.variant} variantLabel={r.variant_label} />
                        ) : (
                          // Quietly blank on the list, explicit on the run
                          // page: here it is one row among many from an older
                          // harness, there it is the question being asked.
                          <span className="text-slate-300 dark:text-slate-600">
                            {t("common.dash")}
                          </span>
                        )}
                      </td>
                    )}
                    <td className="whitespace-nowrap px-3 py-2 text-slate-600 dark:text-slate-400">
                      {on(r.started_at)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-slate-600 dark:text-slate-400">
                      {duration(r.started_at, r.finished_at)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.step_count}</td>
                    <td className="px-3 py-2"><VerdictBar run={r} /></td>
                  </tr>

                    ))}
                  </Fragment>
                ))}
              </Day>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="space-y-2 sm:hidden">
        {shown.map((r) => <RunCard key={r.id} run={r} bench showRef={refNeeded(shown).has(r.id)} />)}
      </ul>


      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-500">
        <span>
          {/* The head already answers how many are loaded, so this line speaks
              only when it has something the head does not: a filtered subset,
              or more waiting on the server. */}
          {filtering
            ? `${t("runs.loadedFiltered", { shown: shown.length, total: runs.length })}${
                more ? t("runs.moreOnServer") : ""
              }`
            : more
              ? `${t("runs.loaded", { count: runs.length })}${t("runs.moreOnServer")}`
              : ""}
        </span>
        {filtering && (
          <button type="button" className="underline"
                  onClick={() => { setQ(""); setOutcome(""); setScope(""); }}>
            {t("runs.clearFilters")}
          </button>
        )}
        {more && (
          <button type="button" className="btn-quiet" onClick={() => setLimit(limit + PAGE)}>
            {t("runs.loadMore", { count: PAGE })}
          </button>
        )}
      </div>
      {filtering && more && (
        // Said out loud: a filtered list that looks complete is worse than an
        // unfiltered one. The search runs over what is loaded, and what is
        // loaded is the newest N.
        <p className="mt-1 text-xs text-slate-500">
          {t("runs.filteringNote", { count: runs.length })}
        </p>
      )}
    </section>
  );
}

function Chip({
  on: isOn,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={isOn}
      className={`rounded-full border px-3 py-1 text-xs ${
        isOn
          ? "border-brand-700 bg-brand-700 text-white dark:border-sky-300 dark:bg-sky-300 dark:text-slate-900"
          : "border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
      }`}
    >
      {children}
    </button>
  );
}

/** A day heading inside the table body, so ninety rows read as a calendar
    rather than one undifferentiated column of timestamps. */
function Day({ day, span, children }: { day: string; span: number; children: React.ReactNode }) {
  return (
    <>
      <tr>
        <th
          scope="colgroup"
          colSpan={span}
          className="bg-slate-50 px-3 py-1 text-left text-xs font-semibold text-slate-500 dark:bg-slate-800/40"
        >
          {day}
        </th>
      </tr>
      {children}
    </>
  );
}

/** Group by local calendar day, keeping the server's order within each. */
/**
 * Runs of one campaign, side by side.
 *
 * The list is in time order and a campaign runs its stops one after another, so
 * its runs are already adjacent; this only names the run of them. Runs nobody
 * queued as part of a campaign each form a group of their own with no heading,
 * which keeps one map over one shape instead of two branches at the call site.
 */
export function byCampaign(runs: Run[]): { key: string; campaign: number | null; runs: Run[] }[] {
  const out: { key: string; campaign: number | null; runs: Run[] }[] = [];
  for (const r of runs) {
    const c = r.campaign_id ?? null;
    const last = out[out.length - 1];
    if (c !== null && last && last.campaign === c) last.runs.push(r);
    else out.push({ key: `${c ?? "x"}-${r.id}`, campaign: c, runs: [r] });
  }
  return out;
}

export function byDay(runs: Run[], now?: Date): [string, Run[]][] {
  const out: [string, Run[]][] = [];
  for (const r of runs) {
    const key = dayLabel(r.started_at, now);
    const last = out[out.length - 1];
    if (last && last[0] === key) last[1].push(r);
    else out.push([key, [r]]);
  }
  return out;
}


/**
 * The heading over a day's runs.
 *
 * BUILT HERE RATHER THAN BY `toLocaleDateString`, for the same reason `on()`
 * stopped using `dateStyle:"short"`: asking the browser for weekday+day+month
 * returns "Sun, September 6" under en-US and "niedziela, 6 września" under
 * pl-PL, and the first of those puts the number last where every other date on
 * the page puts it first. A heading is not the place to be inconsistent about
 * which number is the day.
 *
 * The words are still the reader's. Weekday and month names come from the
 * catalogue as one space-separated string each, in the order the language reads
 * them - twelve separate keys would be twelve chances to misorder them - and
 * the ARRANGEMENT is a template, so Finnish can keep its ordinal dot without
 * this function knowing which language it is in.
 */
export function dayLabel(ts: number, now = new Date()): string {
  const d = new Date(ts * 1000);
  const same = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (same(d, now)) return i18n.t("runs.today");
  if (same(d, yesterday)) return i18n.t("runs.yesterday");
  const parts = {
    weekday: i18n.t("time.days").split(/\s+/)[d.getDay()],
    day: d.getDate(),
    month: i18n.t("time.months").split(/\s+/)[d.getMonth()],
    year: d.getFullYear(),
  };
  return d.getFullYear() === now.getFullYear()
    ? i18n.t("runs.dayLabel", parts)
    : i18n.t("runs.dayLabelWithYear", parts);
}
